import { useState } from 'react'
import { Box, Button, Grid2, IconButton, Stack, Typography } from '@mui/material'
import { Grid_Gap } from '../../shared/constants'
import CustomDivider from '../../shared/utils/CustomDivider'
import { useAllUserList, useApartmentListForCourse, useRoleList } from '../../assets/api/useAdminApi'
import { useDialog } from '../../shared/hook/useDialog'
import MainDialogBox from '../../shared/components/Dialog/MainDialogBox'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { AutoCompleteField } from '../../shared/components/formfields/AutoCompleteField'
import TableFilterTemplate from '../../shared/utils/TableFilterTemplate'
import { useToaster } from '../../shared/utils/ToasterProvider'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { TextFormField } from '../../shared/components/formfields/TextFormField'
import useAuthStore from '../../store/useAuthStore'
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import { NormalContainerWithHeader } from '../../shared/components/student/NormalComponent'
import AddUserForm from './AddUserForm'
import api from '../../assets/nodeApi/axiosInstance'
import { LocationFilterFields } from '../../shared/components/LocationFilterFields'


const AllUserList = () => {
    const { data: apartmentList, isLoading: isLoadingApartmentList } = useApartmentListForCourse();
    const ApartmentOptions = apartmentList?.data?.map((item, index) => ({
        id: index + 1,
        label: item?.apartment_name,
        value: item?.apartment_name,
    })) || [];

    const [filters, setFilters] = useState({});
    const { data: allUserList, isLoading } = useAllUserList(filters);
    const { data: roleList, isLoading: roleLoading } = useRoleList();
    const queryClient = useQueryClient();

    const roleOptions = roleList?.roles?.map((item, index) => ({
        id: index + 1,
        label: item,
        value: item,
    }));

    const { open, handleClickOpen, handleClose } = useDialog();
    const [selectedRow, setSelectedRow] = useState(null);
    const { token } = useAuthStore();
    const { showToaster } = useToaster();
    const [isAddUser, setIsAddUser] = useState(false);

    const handleClickRowOpen = (row) => {
        setSelectedRow(row);
        handleClickOpen(true);
    };

    // ✅ Called by AddUserForm after a successful user creation
    // Invalidates the query so the table refetches and shows the new user,
    // after which local search & filter work on the fresh data
    const handleUserAdded = () => {
        queryClient.invalidateQueries({ queryKey: ['get-allusers-list'] });
        setIsAddUser(false);
    };

    const alluserColumns = [
        {
            field: "id",
            headerName: "Sr No",
            minWidth: 70,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>
        },
        {
            field: "firstname",
            headerName: "First Name",
            minWidth: 150,
            renderCell: (params) => (
                <Typography variant="body2">
                    {params?.row?.role === "student" ? params?.row?.lastname : params.value}
                </Typography>
            ),
        },
        {
            field: "lastname",
            headerName: "Last Name",
            minWidth: 150,
            renderCell: (params) => (
                <Typography variant="body2">
                    {params?.row?.role === "student" ? "" : params.value}
                </Typography>
            ),
        },
        {
            field: "role",
            headerName: "Role",
            minWidth: 150,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: "username",
            headerName: "Username",
            minWidth: 150,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: "apartment_name",
            headerName: "Apartment Name",
            minWidth: 200,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: "email",
            headerName: "Email",
            minWidth: 200,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: "mobile",
            headerName: "Mobile",
            minWidth: 150,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: "country_name",
            headerName: "Country",
            minWidth: 120,
            renderCell: (params) => <Typography variant="body2">{params.value || ""}</Typography>,
        },
        {
            field: "state",
            headerName: "State",
            minWidth: 120,
            renderCell: (params) => <Typography variant="body2">{params.value || ""}</Typography>,
        },
        {
            field: "city",
            headerName: "City",
            minWidth: 120,
            renderCell: (params) => <Typography variant="body2">{params.value || ""}</Typography>,
        },
        {
            field: "zonal",
            headerName: "Zonal",
            minWidth: 120,
            renderCell: (params) => <Typography variant="body2">{""}</Typography>,
        },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 100,
            renderCell: (params) => (
                <Stack direction="row" spacing={1}>
                    <IconButton
                        onClick={() => handleClickRowOpen(params.row)}
                        size="small"
                    >
                        <ChangeCircleIcon color="text.primary" />
                    </IconButton>
                </Stack>
            ),
        },
    ];

    const mutation = useMutation({
        mutationFn: (formData) => {
            return api.post(`change-password`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
        },
        onSuccess: (response) => {
            showToaster('Success', response?.data?.message, 'success');
        },
        onError: (error) => {
            if (error?.response?.status === 400) {
                showToaster(error.response.data.message, 'Error', 'error');
            } else {
                showToaster('Error Caught While submitting the form', 'Error', 'error');
            }
        },
    });

    const handleSubmit = (values, { setSubmitting, resetForm }) => {
        const formdata = {
            user_id: selectedRow?.id,
            ...values
        };
        mutation.mutate(formdata, {
            onSettled: () => {
                setSubmitting(false);
            },
            onSuccess: () => {
                resetForm();
                handleClose();
            }
        });
    };

    return (
        <Box>
            <NormalContainerWithHeader
                headerText={isAddUser ? "Add User" : ""}
                RightContent={
                    !isAddUser && (
                        <Button variant="outlined" onClick={() => setIsAddUser(true)}>
                            Add User
                        </Button>
                    )
                }
            >
                <CustomDivider />
                {isAddUser && (
                    <AddUserForm
                        // ✅ Pass onSuccess so after 201 the table refetches
                        onSuccess={handleUserAdded}
                        // ✅ Also close the form if user clicks Cancel inside AddUserForm
                        onClose={() => setIsAddUser(false)}
                    />
                )}
            </NormalContainerWithHeader>

            <CustomDivider />

            <TableFilterTemplate
                title="All User List"
                columns={alluserColumns}
                rowsData={allUserList?.data || []}
                isLoading={isLoading}
                filterInitialValues={filters}
                filterReset={() => setFilters({})}
                onApplyFilters={(f) => setFilters(f)}
                renderFilterFields={() => (
                    <>
                        <Grid2 children size={12}>
                            <Field
                                name="role"
                                component={AutoCompleteField}
                                placeholder="Select Role"
                                options={roleOptions}
                                label="Role"
                                isLoading={roleLoading}
                            />
                        </Grid2>
                        <Grid2 children size={12}>
                            <Field
                                name="apartment_name"
                                component={AutoCompleteField}
                                placeholder="Apartment Name"
                                options={ApartmentOptions}
                                label="Apartment Name"
                                isLoading={isLoadingApartmentList}
                            />
                        </Grid2>
                        <LocationFilterFields
                            countryField="country"
                            stateField="state"
                            cityField="city"
                            isStoredId={false}
                        />
                    </>
                )}
            />

            <MainDialogBox open={open} handleClose={handleClose} width={"40%"}>
                <Formik
                    initialValues={{
                        new_password: "",
                        new_password_confirmation: ""
                    }}
                    enableReinitialize={true}
                    validationSchema={
                        Yup.object().shape({
                            new_password: Yup
                                .string()
                                .min(8, "Password must be at least 8 characters")
                                .max(16, "Password must be at most 16 characters")
                                .required("Password is required"),
                            new_password_confirmation: Yup
                                .string()
                                .oneOf([Yup.ref("new_password"), null], "Passwords must match")
                                .required("Confirm Password is required"),
                        })
                    }
                    onSubmit={handleSubmit}
                >
                    {() => (
                        <Form>
                            <Grid2 container spacing={Grid_Gap}>
                                <Grid2 children size={12}>
                                    <Field
                                        name="new_password"
                                        component={TextFormField}
                                        label="New Password"
                                        isRequired={true}
                                        type="password"
                                    />
                                </Grid2>
                                <Grid2 children size={12}>
                                    <Field
                                        name="new_password_confirmation"
                                        component={TextFormField}
                                        label="Confirm Password"
                                        isRequired={true}
                                        type="password"
                                    />
                                </Grid2>
                                <Grid2 children textAlign={"end"} size={12}>
                                    <Button variant='contained' type='submit' fullWidth>Submit</Button>
                                </Grid2>
                            </Grid2>
                        </Form>
                    )}
                </Formik>
            </MainDialogBox>
        </Box>
    );
};

export default AllUserList;